<template>
  <div>
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Business Wallet</h5>
        <p class="page-sub">Manage your virtual account and track incoming transfers</p>
      </div>
      <div class="page-actions">
        <button class="btn-outline btn-sm" @click="loadWallet" :disabled="loading">
          <font-awesome-icon icon="sync-alt" :spin="loading" /> Refresh
        </button>
        <button
          v-if="!loading && !wallet && canCreate"
          class="btn-primary-action"
          @click="showModal = true"
        >
          <font-awesome-icon icon="plus" /> Create Wallet
        </button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="space-y">
      <div class="skeleton skeleton--tall"></div>
      <div class="skeleton"></div>
    </div>

    <template v-else>

      <!-- Empty state (no wallet) -->
      <div v-if="!wallet" class="empty-dashed">
        <div class="empty-dashed-icon">
          <font-awesome-icon icon="wallet" />
        </div>
        <p class="empty-dashed-title">No wallet yet</p>
        <p class="empty-dashed-sub">Create a virtual account to start receiving transfers from customers.</p>
        <button v-if="canCreate" class="btn-primary-action mt-12" @click="showModal = true">
          <font-awesome-icon icon="plus" /> Create Wallet
        </button>
      </div>

      <template v-else>

        <!-- Balance card (gradient) -->
        <div class="balance-card">
          <div class="balance-circle balance-circle--tr"></div>
          <div class="balance-circle balance-circle--bl"></div>

          <p class="balance-label">Available Balance</p>
          <p class="balance-amount">{{ $filters.formatMoney(wallet.balance, company?.currency?.symbol) }}</p>

          <div class="balance-meta">
            <font-awesome-icon icon="university" class="balance-meta-icon" />
            <span class="balance-bank">{{ wallet.bank_name }}</span>
            <span class="balance-sep">·</span>
            <span class="balance-acct">{{ wallet.account_number }}</span>
            <button v-if="wallet.account_number" class="copy-btn-sm" @click="copyText(wallet.account_number, 'acct')">
              <font-awesome-icon :icon="copiedAcct ? 'check' : 'copy'" />
              {{ copiedAcct ? 'Copied' : 'Copy' }}
            </button>
          </div>

          <span class="balance-badge" :class="wallet.enabled ? 'balance-badge--active' : 'balance-badge--inactive'">
            {{ wallet.enabled ? 'Active' : 'Inactive' }}
          </span>
        </div>

        <!-- Account Details card -->
        <div class="info-card mt-14">
          <div class="info-card-header">
            <div class="info-card-icon">
              <font-awesome-icon icon="building" />
            </div>
            <h6 class="info-card-title">Account Details</h6>
          </div>
          <div class="info-card-body">
            <div class="info-row"><span class="info-label">Account Name</span><span class="info-value">{{ wallet.account_name }}</span></div>
            <div class="info-row"><span class="info-label">Account Number</span><span class="info-value info-value--mono">{{ wallet.account_number || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Bank</span><span class="info-value">{{ wallet.bank_name || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Bank Code</span><span class="info-value">{{ wallet.bank_code || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Provider</span><span class="info-value">{{ wallet.provider || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Payable Code</span><span class="info-value info-value--mono info-value--sm">{{ wallet.payable_code || '—' }}</span></div>
            <div class="info-row"><span class="info-label">Merchant Code</span><span class="info-value">{{ wallet.merchant_code || '—' }}</span></div>
          </div>
        </div>

      </template>

      <!-- Webhook card — always visible -->
      <div class="info-card mt-14">
        <div class="info-card-header">
          <div class="info-card-icon info-card-icon--amber">
            <font-awesome-icon icon="exclamation-circle" />
          </div>
          <h6 class="info-card-title">Webhook Endpoint</h6>
        </div>
        <div class="info-card-body">
          <p class="webhook-desc">
            Register this URL in your Interswitch merchant dashboard under
            <strong>Webhook / Notification URL</strong>. Interswitch will POST a
            <code class="inline-code">TRANSACTION.COMPLETED</code>
            event to this endpoint whenever a transfer is received.
          </p>
          <div class="webhook-strip">
            <code class="webhook-url">{{ webhookUrl }}</code>
            <button class="copy-btn" @click="copyText(webhookUrl, 'webhook')" :class="copiedWebhook ? 'copy-btn--done' : ''">
              <font-awesome-icon :icon="copiedWebhook ? 'check' : 'copy'" />
              {{ copiedWebhook ? 'Copied!' : 'Copy' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Transactions card — only when wallet exists -->
      <template v-if="wallet">
        <div class="info-card mt-14">
          <div class="info-card-header" style="justify-content:space-between">
            <div style="display:flex;align-items:center;gap:10px">
              <div class="info-card-icon info-card-icon--purple">
                <font-awesome-icon icon="exchange-alt" />
              </div>
              <div>
                <h6 class="info-card-title" style="margin:0">Transaction History</h6>
                <p class="info-card-sub">Incoming transfers recorded from webhook events</p>
              </div>
            </div>
            <button class="btn-outline btn-sm" @click="loadTransactions(1)" :disabled="txLoading">
              <font-awesome-icon icon="sync-alt" :spin="txLoading" />
            </button>
          </div>

          <!-- Loading -->
          <div v-if="txLoading" class="state-block" style="padding:40px">
            <div class="spinner-border text-primary" role="status"></div>
          </div>

          <!-- Empty -->
          <div v-else-if="!transactions.length" class="state-block">
            <div class="state-icon state-icon--blue">
              <font-awesome-icon icon="inbox" />
            </div>
            <p class="state-title">No transactions yet</p>
            <p class="state-sub">Transactions will appear here once payments are received.</p>
          </div>

          <!-- Table -->
          <template v-else>
            <div class="table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Channel</th>
                    <th>Bank</th>
                    <th>Amount</th>
                    <th>Remittance</th>
                    <th>Description</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in transactions" :key="tx.id">
                    <td class="td-nowrap">{{ fmtDate(tx.created_at) }}</td>
                    <td>{{ tx.merchant_customer_name || '—' }}</td>
                    <td><span class="channel-chip">{{ tx.channel || '—' }}</span></td>
                    <td>{{ tx.bank_code || '—' }}</td>
                    <td class="td-amount">{{ $filters.formatMoney(tx.amount / 100, company?.currency?.symbol) }}</td>
                    <td>{{ $filters.formatMoney((tx.remittance_amount || 0) / 100, company?.currency?.symbol) }}</td>
                    <td class="td-ellipsis">{{ tx.response_description || '—' }}</td>
                    <td><span class="status-badge status-badge--green">{{ tx.status }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="txMeta && txMeta.last_page > 1" class="table-footer">
              <button class="btn-outline btn-sm" :disabled="txPage <= 1" @click="loadTransactions(txPage - 1)">
                <font-awesome-icon icon="chevron-left" /> Prev
              </button>
              <span class="page-label">Page {{ txPage }} of {{ txMeta.last_page }}</span>
              <button class="btn-outline btn-sm" :disabled="txPage >= txMeta.last_page" @click="loadTransactions(txPage + 1)">
                Next <font-awesome-icon icon="chevron-right" />
              </button>
            </div>
          </template>

        </div>
      </template>

    </template>

    <!-- Create Wallet Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-hd">
          <div class="modal-hd-icon">
            <font-awesome-icon icon="wallet" />
          </div>
          <div>
            <h2 class="modal-title">Create Business Wallet</h2>
            <p class="modal-sub">A permanent virtual account will be generated via Interswitch. Funds transferred to this account will be credited to your wallet automatically.</p>
          </div>
        </div>

        <div class="modal-form">
          <div class="field-group">
            <label class="field-label">Account Name</label>
            <input type="text" class="field-input" v-model="form.account_name" placeholder="e.g. Acme Corp" />
          </div>
          <div class="field-group">
            <label class="field-label">Bank Provider</label>
            <select class="field-input" v-model="form.provider">
              <option value="WEMA">Wema Bank</option>
              <option value="9PSB">9 Payment Service Bank</option>
              <option value="FBP">Fidelity Bank</option>
            </select>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn-outline flex-1" @click="showModal = false">Cancel</button>
            <button type="button" class="btn-primary-action flex-1 justify-content-center" @click="createWallet" :disabled="creating">
              <font-awesome-icon :icon="creating ? 'spinner' : 'check'" :spin="creating" />
              {{ creating ? 'Creating…' : 'Create Wallet' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import { reusableMixin } from '../../../lib/reusableMixin';

  export default {
    name: 'BusinessWallet',
    mixins: [reusableMixin],

    data() {
      return {
        toast:         useToast(),
        store:         useStore(),
        loading:       true,
        creating:      false,
        txLoading:     false,
        copiedWebhook: false,
        copiedAcct:    false,
        showModal:     false,
        wallet:        null,
        transactions:  [],
        txMeta:        null,
        txPage:        1,
        company:       null,
        form: { account_name: '', provider: 'WEMA' },
      };
    },

    computed: {
      currentUser() { return this.store.state.getLoggedInUser; },
      permission()  { return this.store.state.getCurrentUserPermission; },
      canCreate()   { return this.currentUser?.is_super === 1 || this.permission?.create_wallet; },
      webhookUrl() {
        const base = axios.defaults.baseURL?.replace(/\/api\/?$/, '') ?? '';
        const code = this.$route.params.companyCode || localStorage.getItem('companyCode') || '';
        return code ? `${base}/api/${code}/wallet/webhook` : `${base}/api/wallet/webhook`;
      },
    },

    async created() {
      await this.loadActiveBusiness();
      await this.loadWallet();
    },

    methods: {
      async loadActiveBusiness() {
        try {
          const res = await axios.get('/admin/business/my');
          const active = res.data.data?.active;
          if (active?.name) this.form.account_name = active.name;
        } catch (_) { /* silently ignore — account name will default to empty */ }
      },

      async loadWallet() {
        this.loading = true;
        try {
          const res  = await axios.get('wallet');
          this.wallet = res.data.data;
          if (this.wallet) await this.loadTransactions(1);
        } catch (e) {
          if (e.response?.status !== 403) {
            this.toast.error(e.response?.data?.message ?? 'Failed to load wallet');
          }
        } finally {
          this.loading = false;
        }
      },

      async loadTransactions(page = 1) {
        this.txLoading = true;
        this.txPage    = page;
        try {
          const res = await axios.get('wallet/transactions', { params: { page, length: 15 } });
          const pd  = res.data.data;
          this.transactions = pd.data ?? [];
          this.txMeta       = { last_page: pd.last_page, total: pd.total };
        } catch (e) {
          this.toast.error(e.response?.data?.message ?? 'Failed to load transactions');
        } finally {
          this.txLoading = false;
        }
      },

      async createWallet() {
        if (!this.form.account_name?.trim()) {
          this.toast.error('Account name is required');
          return;
        }
        this.creating = true;
        try {
          const res  = await axios.post('wallet/create', this.form);
          this.wallet = res.data.data;
          this.showModal = false;
          this.toast.success('Virtual account created successfully');
          await this.loadTransactions(1);
        } catch (e) {
          this.toast.error(e.response?.data?.message ?? 'Failed to create wallet');
        } finally {
          this.creating = false;
        }
      },

      async copyText(text, target) {
        try {
          await navigator.clipboard.writeText(text);
          if (target === 'webhook') {
            this.copiedWebhook = true;
            setTimeout(() => { this.copiedWebhook = false; }, 2500);
          } else {
            this.copiedAcct = true;
            setTimeout(() => { this.copiedAcct = false; }, 2500);
          }
        } catch (_) {
          this.toast.error('Could not copy to clipboard');
        }
      },

      fmtDate(str) {
        if (!str) return '—';
        return new Date(str).toLocaleString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      },
    },
  };
</script>

<style scoped>
/* ── Shell ──────────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.mt-14 { margin-top:14px; }
.mt-12 { margin-top:12px; }

/* ── Buttons ────────────────────────────────────────────────────────────── */
.btn-primary-action {
  display:inline-flex; align-items:center; gap:7px;
  background:#004AAD; color:#fff; font-size:13px; font-weight:600;
  padding:9px 18px; border-radius:8px; border:none; cursor:pointer;
  transition:background .15s; white-space:nowrap;
}
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-primary-action.flex-1 { flex:1; justify-content:center; }
.btn-outline {
  display:inline-flex; align-items:center; gap:6px;
  background:#fff; color:#1a2b45; border:1px solid #dde5f0;
  font-size:13px; font-weight:500; padding:7px 14px; border-radius:8px;
  cursor:pointer; transition:background .15s;
}
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.btn-outline:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline.flex-1  { flex:1; justify-content:center; }
.btn-sm { padding:5px 12px; font-size:12px; }

/* ── Skeletons ──────────────────────────────────────────────────────────── */
.space-y > * + * { margin-top:14px; }
.skeleton {
  background:linear-gradient(90deg,#f0f3f8 25%,#e8ecf4 50%,#f0f3f8 75%);
  background-size:200% 100%; animation:shimmer 1.4s infinite;
  border-radius:16px; height:80px;
}
.skeleton--tall { height:180px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── Empty dashed ───────────────────────────────────────────────────────── */
.empty-dashed {
  border:2px dashed #dde5f0; border-radius:16px; background:#fff;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:56px 24px; text-align:center;
}
.empty-dashed-icon {
  width:56px; height:56px; border-radius:16px;
  background:#e8f0fe; color:#004AAD; font-size:22px;
  display:flex; align-items:center; justify-content:center;
  margin-bottom:14px;
}
.empty-dashed-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.empty-dashed-sub   { font-size:13px; color:#7a90b0; margin:0; }

/* ── Balance gradient card ──────────────────────────────────────────────── */
.balance-card {
  position:relative; overflow:hidden; border-radius:20px;
  background:linear-gradient(135deg, #004AAD 0%, #1a6fde 100%);
  padding:28px 28px 24px; color:#fff;
  box-shadow:0 8px 32px rgba(0,74,173,.25);
}
.balance-circle {
  position:absolute; border-radius:50%; background:rgba(255,255,255,.1);
}
.balance-circle--tr {
  width:180px; height:180px;
  top:-60px; right:-60px;
}
.balance-circle--bl {
  width:120px; height:120px;
  bottom:-40px; left:-40px;
}
.balance-label {
  position:relative; font-size:11px; font-weight:700; text-transform:uppercase;
  letter-spacing:.08em; color:rgba(255,255,255,.65); margin:0 0 6px;
}
.balance-amount {
  position:relative; font-size:36px; font-weight:700; letter-spacing:-.5px;
  margin:0 0 16px; line-height:1.1;
}
.balance-meta {
  position:relative; display:flex; align-items:center; flex-wrap:wrap; gap:8px;
  font-size:13px;
}
.balance-meta-icon { color:rgba(255,255,255,.6); font-size:12px; }
.balance-bank  { color:rgba(255,255,255,.8); }
.balance-sep   { color:rgba(255,255,255,.3); }
.balance-acct  { font-family:'Courier New',monospace; font-size:15px; font-weight:700; letter-spacing:.05em; }
.balance-badge {
  position:relative; display:inline-block; margin-top:14px;
  padding:3px 12px; border-radius:20px; font-size:11px; font-weight:700;
}
.balance-badge--active   { background:rgba(255,255,255,.2); color:#fff; }
.balance-badge--inactive { background:rgba(255,100,100,.25); color:#ffcccc; }

.copy-btn-sm {
  display:inline-flex; align-items:center; gap:5px;
  background:rgba(255,255,255,.15); border:1px solid rgba(255,255,255,.25);
  color:#fff; font-size:11px; font-weight:600; padding:3px 10px;
  border-radius:7px; cursor:pointer; transition:background .15s;
}
.copy-btn-sm:hover { background:rgba(255,255,255,.25); }

/* ── Info card ──────────────────────────────────────────────────────────── */
.info-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,.03); }
.info-card-header {
  display:flex; align-items:center; gap:10px;
  padding:14px 18px; border-bottom:1px solid #f0f3f8;
}
.info-card-icon {
  width:34px; height:34px; border-radius:9px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:14px;
  background:#e8f0fe; color:#004AAD;
}
.info-card-icon--amber  { background:#fff7e6; color:#c97d00; }
.info-card-icon--purple { background:rgba(124,58,237,.1); color:#7c3aed; }
.info-card-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }
.info-card-sub   { font-size:11px; color:#7a90b0; margin:0; }
.info-card-body  { padding:0 18px; }

/* ── Info rows ──────────────────────────────────────────────────────────── */
.info-row {
  display:flex; align-items:center; justify-content:space-between;
  border-bottom:1px solid #f6f8fb; padding:11px 0;
}
.info-row:last-child { border-bottom:none; }
.info-label { font-size:13px; color:#7a90b0; }
.info-value { font-size:13px; font-weight:600; color:#1a2b45; text-align:right; }
.info-value--mono { font-family:'Courier New',monospace; font-size:13px; color:#004AAD; }
.info-value--sm   { font-size:11px; max-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* ── Webhook ────────────────────────────────────────────────────────────── */
.webhook-desc {
  font-size:12px; color:#7a90b0; margin:0 0 12px; line-height:1.6;
}
.webhook-strip {
  display:flex; align-items:center; gap:10px;
  background:#f4f7fb; border:1px solid #e4eaf2; border-radius:10px;
  padding:10px 14px;
}
.webhook-url {
  flex:1; font-family:'Courier New',monospace; font-size:12px; color:#1a2b45;
  background:none; border:none; word-break:break-all;
}
.copy-btn {
  display:inline-flex; align-items:center; gap:6px; flex-shrink:0;
  background:#004AAD; color:#fff; font-size:12px; font-weight:600;
  padding:6px 14px; border-radius:7px; border:none; cursor:pointer;
  transition:background .15s;
}
.copy-btn:hover { background:#003b8e; }
.copy-btn--done { background:#1e7e34; }
.inline-code { background:#e8f0fe; color:#004AAD; padding:1px 5px; border-radius:4px; font-size:11px; font-family:monospace; }

/* ── Table ──────────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table th {
  padding:10px 16px; font-size:11px; font-weight:700; color:#7a90b0;
  text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #e4eaf2; white-space:nowrap;
}
.data-table td { padding:12px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f3f5fa; }
.data-table tbody tr:last-child td { border-bottom:none; }
.data-table tbody tr:hover { background:#fafcff; }
.td-nowrap   { white-space:nowrap; }
.td-amount   { font-weight:700; color:#004AAD; white-space:nowrap; }
.td-ellipsis { max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.table-footer {
  padding:12px 18px; border-top:1px solid #e4eaf2;
  display:flex; align-items:center; justify-content:flex-end; gap:10px;
}
.page-label { font-size:13px; color:#7a90b0; }

/* ── State block (empty transactions) ──────────────────────────────────── */
.state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px 24px; text-align:center; }
.state-icon   { width:56px; height:56px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:22px; margin-bottom:14px; }
.state-icon--blue { background:#e8f0fe; color:#004AAD; }
.state-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 5px; }
.state-sub    { font-size:13px; color:#7a90b0; margin:0; }

/* ── Chips ──────────────────────────────────────────────────────────────── */
.channel-chip { background:#e8f0fe; color:#004AAD; padding:2px 9px; border-radius:20px; font-size:11px; font-weight:600; }
.status-badge { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.status-badge--green { background:#e6f4ea; color:#1e7e34; }

/* ── Modal ──────────────────────────────────────────────────────────────── */
.modal-overlay {
  position:fixed; inset:0; z-index:9999;
  background:rgba(0,0,0,.45); backdrop-filter:blur(3px);
  display:flex; align-items:center; justify-content:center; padding:20px;
}
.modal-box {
  background:#fff; border-radius:20px; padding:28px;
  width:100%; max-width:440px; box-shadow:0 24px 64px rgba(0,0,0,.15);
}
.modal-hd { display:flex; align-items:flex-start; gap:14px; margin-bottom:22px; }
.modal-hd-icon {
  width:38px; height:38px; border-radius:10px; flex-shrink:0;
  background:#004AAD; color:#fff; font-size:15px;
  display:flex; align-items:center; justify-content:center;
}
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 5px; }
.modal-sub   { font-size:12px; color:#7a90b0; margin:0; line-height:1.6; }
.modal-form  { display:flex; flex-direction:column; gap:16px; }
.field-group { display:flex; flex-direction:column; gap:6px; }
.field-label { font-size:13px; font-weight:600; color:#1a2b45; }
.field-input {
  padding:9px 12px; font-size:13px; color:#1a2b45;
  border:1px solid #dde5f0; border-radius:9px; background:#f8fafc;
  outline:none; transition:border-color .15s; width:100%; box-sizing:border-box;
}
.field-input:focus { border-color:#004AAD; background:#fff; }
.modal-actions { display:flex; gap:10px; margin-top:4px; }
</style>
